import { useRef } from 'react';
import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import { HeatmapChart } from '@/components/analytics/charts/HeatmapChart';
import { useChartDimensions } from '@/hooks/useChartDimensions';
import type { ActivityHeatmapData } from '@/types/analytics';
import styles from './CalendarHeatmap.module.scss';

export interface CalendarHeatmapProps {
  data: ActivityHeatmapData[] | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Calendar Heatmap Widget
 *
 * GitHub-style contribution calendar showing daily activity.
 * Color intensity represents session count or success rate.
 */
export function CalendarHeatmap({ data, isLoading, error }: CalendarHeatmapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useChartDimensions(containerRef as any);

  // Transform data to heatmap cells
  const heatmapData = data
    ? data.map((d) => ({
      date: d.date,
      value: d.sessionCount,
      label: `${d.sessionCount} session${d.sessionCount !== 1 ? 's' : ''} - ${(d.avgSuccessRate || 0).toFixed(0)}% success rate`,
    }))
    : [];

  // Calculate stats
  const activeDays = data?.filter((d) => d.sessionCount > 0) || [];
  const activeDaysCount = activeDays.length;

  const maxSessions = data && data.length > 0
    ? Math.max(...data.map((d) => d.sessionCount))
    : 0;

  const avgSuccessRate = activeDaysCount > 0
    ? activeDays.reduce((sum, d) => sum + (d.successRate || 0), 0) / activeDaysCount
    : 0;

  // Calculate academic year range (Aug 1 - July 31)
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  let startYear = now.getFullYear();

  // If before August (month 7), start year is previous year
  if (currentMonth < 7) {
    startYear = startYear - 1;
  }

  const minDate = new Date(startYear, 7, 1); // Aug 1
  const maxDate = new Date(startYear + 1, 6, 31); // July 31

  return (
    <WidgetContainer
      title="Activity Calendar"
      description="Daily practice activity heatmap"
      isLoading={isLoading}
      error={error}
      isEmpty={false} // Always show calendar grid
      size="large"
    >
      <div ref={containerRef} className={styles.calendarHeatmap}>
        {/* Heatmap */}
        {width > 0 && (
          <div className={styles.calendarHeatmap__chart}>
            <HeatmapChart
              data={heatmapData}
              width={width}
              height={150}
              cellSize={12}
              cellGap={2}
              colorScale={['rgba(var(--forest-rgb-500), 0.1)', 'rgba(var(--forest-rgb-500), 1)']}
              showMonthLabels
              showWeekdayLabels
              animate
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        )}

        {/* Legend */}
        <div className={styles.calendarHeatmap__legend}>
          <span className={styles.calendarHeatmap__legendLabel}>Less</span>
          <div className={styles.calendarHeatmap__legendColors}>
            <div
              className={styles.calendarHeatmap__legendColor}
              style={{ backgroundColor: 'rgba(var(--forest-rgb-500), 0.1)' }}
            />
            <div
              className={styles.calendarHeatmap__legendColor}
              style={{ backgroundColor: 'rgba(var(--forest-rgb-500), 0.25)' }}
            />
            <div
              className={styles.calendarHeatmap__legendColor}
              style={{ backgroundColor: 'rgba(var(--forest-rgb-500), 0.5)' }}
            />
            <div
              className={styles.calendarHeatmap__legendColor}
              style={{ backgroundColor: 'rgba(var(--forest-rgb-500), 0.75)' }}
            />
            <div
              className={styles.calendarHeatmap__legendColor}
              style={{ backgroundColor: 'rgba(var(--forest-rgb-500), 1)' }}
            />
          </div>
          <span className={styles.calendarHeatmap__legendLabel}>More</span>
        </div>

        {/* Stats */}
        <div className={styles.calendarHeatmap__stats}>
          <div className={styles.calendarHeatmap__stat}>
            <span className={styles.calendarHeatmap__statValue}>{activeDaysCount}</span>
            <span className={styles.calendarHeatmap__statLabel}>Active Days</span>
          </div>
          <div className={styles.calendarHeatmap__stat}>
            <span className={styles.calendarHeatmap__statValue}>{maxSessions}</span>
            <span className={styles.calendarHeatmap__statLabel}>Best Day</span>
          </div>
          <div className={styles.calendarHeatmap__stat}>
            <span className={styles.calendarHeatmap__statValue}>
              {avgSuccessRate.toFixed(0)}%
            </span>
            <span className={styles.calendarHeatmap__statLabel}>Avg Success</span>
          </div>
        </div>
      </div>
    </WidgetContainer>
  );
}
